// @flow

import * as React from 'react';
import {
    View, useWindowDimensions,
} from 'react-native';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { APIContext, withConfirmation, Confirmation } from 'speed-js-react';
import { useNavigation } from '@react-navigation/native';
import { navigateWithoutHistory } from 'src/navigation/Stack';
import { HOME_SCREEN } from 'src/navigation/constants';
import Title from 'src/components/Title';
import { pageStyles } from 'src/navigation/defaultPageStyles';
import ProfileInputsForm from 'src/navigation/Profile/ProfileInputsForm';
import ProfileWrapper from 'src/navigation/Profile/ProfileWrapper';
import RemainingChannelsTable from 'src/navigation/Profile/RemainingChannelsTable';
import AddedChannelsTable from 'src/navigation/Profile/AddedChannelsTable';
import ProfileDevices from 'src/navigation/Profile/ProfileDevices';

type Props = {
    route: Object,
    confirm: (confirmation: Confirmation) => void,
};

const Profile = (props: Props) => {
    const { route, confirm } = props;
    const { width } = useWindowDimensions();
    const { apiClient } = React.useContext(APIContext);
    const navigation = useNavigation();

    const channelsErrorRef = React.useRef(null);
    const nameOrPinCodeViewRef = React.useRef(null);

    const [currentProfile, setCurrentProfile] = React.useState(null);
    const [channels, setAllChannels] = React.useState([]);

    const [addedChannels, setAddedChannels] = React.useState([]);
    const [remainingChannels, setRemainingChannels] = React.useState([]);
    const [profileDevices, setProfileDevices] = React.useState([]);

    const [hasChange, setHasChange] = React.useState(false);
    const [resetOrder, setResetOrder] = React.useState(false);
    const [fetching, setFetching] = React.useState(true);
    const [errors, setErrors] = React.useState(null);

    const profileUuid = route.params?.uuid;

    const [values, setValues] = React.useState(
        {
            name: '',
            pin_code: null,
            avatar: profileUuid ? null : 'profile',
        },
    );

    const messages = defineMessages({
        required: {
            id: 'field-required',
            defaultMessage: 'This field is required',
        },
    });

    const { formatMessage } = useIntl();

    React.useEffect(() => {
        document.getElementById('page-description').setAttribute('content', `
        Създаване на профили, добавяне и подредба на канали.
        `);
    }, []);

    React.useEffect(() => {
        if (!fetching) {
            setHasChange(true);
        }
        return () => {
            setHasChange(false);
        };
        // eslint-disable-next-line
    }, [values, addedChannels]);

    React.useEffect(() => {
        if (profileUuid) {
            apiClient.resources.profiles.get(profileUuid).then((profile) => setCurrentProfile(profile));
        }
    }, [apiClient.resources.profiles, profileUuid]);

    const accountUUUD = localStorage.getItem('account');

    React.useEffect(() => {
        apiClient.resources.channels.list({ limit: 300, account_uuid: accountUUUD })
            .then((res) => {
                if (res) {
                    setAllChannels(res.results);
                }
                if (route.params?.uuid) {
                    apiClient.resources.profiles.get(route.params.uuid)
                        .then((profile) => {
                            setValues({ name: profile.name, pin_code: profile.pin_code, avatar: profile.avatar });
                            apiClient.resources.profiles.channels.list(profile.uuid, {
                                limit: 300,
                                account_uuid: accountUUUD,
                            })
                                .then((profileChannels) => {
                                    const result1 = res.results.filter(
                                        (el) => !profile.channels.includes(el.uuid),
                                    );
                                    setRemainingChannels(result1);
                                    const result2 = profileChannels.results.sort(
                                        (a, b) => a.sequence - b.sequence,
                                    ).map((el) => el.channel);
                                    setAddedChannels(result2);
                                    setFetching(false);
                                });
                        });
                } else {
                    setFetching(false);
                    setRemainingChannels(res.results);
                }
            });
    }, [apiClient.resources.channels, apiClient.resources.profiles, accountUUUD, route.params?.uuid]);

    const handleRequestErrors = React.useCallback((err) => {
        if (err.status === 400) {
            setErrors(err.data);
            if ((err.data.name || err.data.pin_code) && nameOrPinCodeViewRef.current) {
                // Scroll to name and pin code view ref
                nameOrPinCodeViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (err.data.channels && channelsErrorRef.current) {
                // Scroll to channel errors ref
                channelsErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (err.data.non_field_errors) {
                setErrors({ name: err.data.non_field_errors });
                if (nameOrPinCodeViewRef.current) {
                    // Scroll to name and pin code view ref
                    nameOrPinCodeViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }, []);

    const onDeleteHandler = () => {
        confirm({
            message: (
                <FormattedMessage
                    id="delete-profile-title"
                    defaultMessage="Are you sure you want to delete the profile?"
                />),
            actions: [
                {
                    id: 'cancel',
                    text: <FormattedMessage id="cancel" defaultMessage="Cancel" />,
                    callback: () => {},
                    buttonStyle: { color: '#abcdef' },
                },
                {
                    id: 'confirm',
                    text: <FormattedMessage id="confirm" defaultMessage="Confirm" />,
                    callback: () => {
                        apiClient.resources.profiles.delete(profileUuid)
                            .then(() => {
                                navigateWithoutHistory(navigation, HOME_SCREEN, { profileUpdated: true });
                                setHasChange(false);
                            })
                            .catch((err) => handleRequestErrors(err));
                    },
                    buttonStyle: { color: 'red' },
                },
            ],
        });
    };

    const handleSubmit = React.useCallback(() => {
        const data = {
            ...values,
            channels: addedChannels.map((el) => el.uuid),
            account: localStorage.getItem('account'),
        };
        if (!values.name || values.name === '') {
            setErrors({ name: formatMessage(messages.required) });
        } else if (profileUuid) {
            apiClient.resources.profiles.partialUpdate(profileUuid, data)
                .then(() => {
                    if (resetOrder) {
                        apiClient.resources.profiles.resetOrder(profileUuid);
                    }
                    apiClient.resources.profiles.addToDevices(profileUuid, profileDevices);
                    navigateWithoutHistory(navigation, HOME_SCREEN, { profileUpdated: true });
                    setHasChange(false);
                })
                .catch((err) => handleRequestErrors(err));
        } else {
            apiClient.resources.profiles.create(data)
                .then((res) => {
                    apiClient.resources.profiles.addToDevices(res.uuid, profileDevices);
                    navigateWithoutHistory(navigation, HOME_SCREEN, { profileUpdated: true });
                    setHasChange(false);
                })
                .catch((err) => handleRequestErrors(err));
        }
    }, [
        values, addedChannels, profileUuid, apiClient.resources.profiles, profileDevices, navigation,
        handleRequestErrors, resetOrder, messages.required, formatMessage,
    ]);

    return (
        !profileUuid ? (
            <View style={pageStyles.external_wrapper}>
                <ProfileWrapper
                    allowSave={addedChannels.length > 0}
                    hasChange={hasChange}
                    values={values}
                    onSaveHandler={handleSubmit}
                    onDeleteHandler={onDeleteHandler}
                    editing={false}
                    currentProfile={null}
                >
                    <Title
                        text={(
                            <FormattedMessage
                                id="create-profile"
                                defaultMessage="Create Profile"
                            />
                        )}
                    />
                    <View style={width < 600 && pageStyles.phone_wrapper}>
                        <ProfileInputsForm
                            handleSubmit={handleSubmit}
                            onChange={setValues}
                            errors={errors}
                            nameOrPinCodeViewRef={nameOrPinCodeViewRef}
                            values={values}
                            editing={false}
                        />
                        <ProfileDevices
                            width={width}
                            accountUUUD={accountUUUD}
                            profileUuid={profileUuid}
                            setProfileDevices={setProfileDevices}
                            setHasChange={setHasChange}
                            profileDevices={profileDevices}
                        />
                        <AddedChannelsTable
                            width={width}
                            currentProfile={currentProfile}
                            addedChannels={addedChannels}
                            channelsErrorRef={channelsErrorRef}
                            setAddedChannels={setAddedChannels}
                            setRemainingChannels={setRemainingChannels}
                            remainingChannels={remainingChannels}
                            errors={errors}
                            profileUuid={profileUuid}
                            accountUUUD={accountUUUD}
                            channels={channels}
                            setResetOrder={setResetOrder}
                        />
                        {(remainingChannels.length > 0 && !currentProfile?.parent) && (
                            <RemainingChannelsTable
                                remainingChannels={remainingChannels}
                                addedChannels={addedChannels}
                                width={width}
                                setRemainingChannels={setRemainingChannels}
                                setAddedChannels={setAddedChannels}
                            />
                        )}
                    </View>
                </ProfileWrapper>
            </View>
        ) : (
            <View style={pageStyles.external_wrapper}>
                {currentProfile && (
                    <ProfileWrapper
                        allowSave={addedChannels.length > 0}
                        hasChange={hasChange}
                        values={values}
                        onSaveHandler={handleSubmit}
                        onDeleteHandler={onDeleteHandler}
                        editing
                        currentProfile={currentProfile}
                    >
                        <Title
                            text={(
                                <FormattedMessage
                                    id="edit-profile"
                                    defaultMessage="Edit Profile"
                                />
                            )}
                        />
                        <View style={width < 600 && pageStyles.phone_wrapper}>
                            <ProfileInputsForm
                                handleSubmit={handleSubmit}
                                onChange={setValues}
                                errors={errors}
                                nameOrPinCodeViewRef={nameOrPinCodeViewRef}
                                values={values}
                                isDefault={currentProfile.parent}
                                editing
                            />
                            <ProfileDevices
                                width={width}
                                accountUUUD={accountUUUD}
                                profileUuid={profileUuid}
                                setProfileDevices={setProfileDevices}
                                setHasChange={setHasChange}
                                profileDevices={profileDevices}
                            />
                            <AddedChannelsTable
                                width={width}
                                currentProfile={currentProfile}
                                addedChannels={addedChannels}
                                channelsErrorRef={channelsErrorRef}
                                setAddedChannels={setAddedChannels}
                                setRemainingChannels={setRemainingChannels}
                                remainingChannels={remainingChannels}
                                errors={errors}
                                profileUuid={profileUuid}
                                accountUUUD={accountUUUD}
                                channels={channels}
                                setResetOrder={setResetOrder}
                                isDefault={currentProfile.parent}
                            />
                            {(remainingChannels.length > 0 && !currentProfile?.parent) && (
                                <RemainingChannelsTable
                                    remainingChannels={remainingChannels}
                                    addedChannels={addedChannels}
                                    width={width}
                                    setRemainingChannels={setRemainingChannels}
                                    setAddedChannels={setAddedChannels}
                                />
                            )}
                        </View>
                    </ProfileWrapper>
                )}
            </View>
        )
    );
};

export default withConfirmation(Profile);
